import React, { useEffect } from 'react';
import type { JSX } from 'react/jsx-runtime';
import Chat from './Chat';
import { IContext, IOtherTarget, ParticipantTypes, MessageTypes, TopicTypes, ITopic, IUserInfo } from '../../types';
import SpinnerIcon from '../../icons/SpinnerIcon';
import { useAtomInstance, useAtomValue } from '@zedux/react';
import { activeTopicAtom, activeTopicHistoryEnabledAtom, temporaryTopicAtom } from '../../atoms/atoms';
import { jwtAtom, socketAtom, userInfoAtom } from '../../atoms/socket';
import { keepInstanceAliveAtom } from '@src/atoms/messageReceiverAtom';
import { topicsAtom } from '../../atoms/topicsAtom';
import TemporarySupportLogin from './TemporarySupportLogin';

export default function ChatWrapper(props: { context: IContext, other: IOtherTarget | null, closePopup: (() => void) | null | undefined, helpMode?: boolean }): JSX.Element {
    const socket = useAtomValue(socketAtom)
    const userInfo = useAtomValue(userInfoAtom)
    const jwt = useAtomValue(jwtAtom)
    const jwtInstance = useAtomInstance(jwtAtom)
    const { context, other, helpMode = false } = props
    const temporaryTopic = useAtomInstance(temporaryTopicAtom, [context])
    const activeTopic = useAtomInstance(activeTopicAtom, [context])
    const activeTopicHistoryEnabled = useAtomInstance(activeTopicHistoryEnabledAtom)
    const topicsInstance = useAtomInstance(topicsAtom, [context])
    useAtomValue(keepInstanceAliveAtom, [context]) // we just want to keep messageReceiverAtom alive to keep the socket connection alive, we don't use the value of it here

    console.log('rendering chat wrapper with context ', context, ' and other ', other)

    useEffect(() => {
        const shouldUseExternalPopupHistory = other != null && typeof props.closePopup === 'function'

        activeTopicHistoryEnabled.setState(!shouldUseExternalPopupHistory)

        return () => {
            activeTopicHistoryEnabled.setState(true)
        }
    }, [activeTopicHistoryEnabled, other, props.closePopup])

    const handleTemporarySupportSuccess = (topicId?: number | null, jwt?: string | null) => {
        console.log('temporary support login success, topicId:', topicId, 'jwt:', jwt)
        if (typeof jwt === 'string' && jwt) {
            const sharedWindow = globalThis as typeof globalThis & {
                dusco_config?: Record<string, unknown>,
            };

            sharedWindow.dusco_config = {
                ...(sharedWindow.dusco_config ?? {}),
                chat_jwt: jwt,
            };

            jwtInstance.setState(jwt as any);
        }
    }

    useEffect(() => {
        if (other != null && userInfo && userInfo.userId) {
            const temporary_topic_id = other.user_id == 0 ? 0 : -(other.user_id)
            const realTopicId = topicsInstance.exports.getRealTopicIdByTemporaryId(temporary_topic_id)

            if (realTopicId != null) {
                temporaryTopic.setState(null)
                activeTopic.setState(realTopicId)
                return
            }

            // Create temporary topic
            const currentTemporary = temporaryTopic.getState()
            if (currentTemporary != null && currentTemporary.topic_id == temporary_topic_id) {
                return
            }

            temporaryTopic.setState({
                topic_id: temporary_topic_id,
                topic_name: '',
                last_seen: '',
                topic_type: context === 'support' ? TopicTypes.SUPPORT : TopicTypes.CHAT,
                created_date: Date.now(),
                last_message_text: 'برای شروع گفتگو پیام دهید',
                last_message_type: MessageTypes.TEXT,
                last_message_created_at: Date.now(),
                last_message_deleted: false,
                avatar_url: '',
                other_display: other.user_id == 0 ? 'پشتیبانی' : 'در حال بارگذاری...',
                message_read: true,
                unseen_count: 0,
                participant_type: (context === 'support' ? ParticipantTypes.USER : ParticipantTypes.VENDOR),
                other_participant_type: (context === 'support' ? ParticipantTypes.USER : ParticipantTypes.VENDOR),
                other_user_id: other.user_id,
                temporary_target_user_id: other.user_id,
            } as ITopic)
            console.log('temporary topic created', temporaryTopic.getState())
        }
    }, [activeTopic, context, other, temporaryTopic, topicsInstance, userInfo])

    if (helpMode && !jwt && context === 'support') {
        return <TemporarySupportLogin onSuccess={handleTemporarySupportSuccess} />
    }

    if (!userInfo) {
        return (
            <div className="flex flex-col justify-center items-center bg-white/50 p-8 w-full h-full">
                <div className="mb-4 size-8 text-blue-500 animate-spin">
                    <SpinnerIcon />
                </div>
                <div color="textSecondary">
                    در حال اتصال...
                </div>
            </div>
        );
    }

    return <>
        <Chat {...props} userInfo={userInfo} />
        {socket == null &&
            <div className='z-9999999 absolute inset-0 flex flex-col justify-center items-center bg-white/50 backdrop-blur-xs'>
                <div className="fill-accent mb-4 size-8 animate-spin">
                    <SpinnerIcon />
                </div>
                <div className='max-w-sm text-center text-pretty'>
                    در حال تلاش برای اتصال مجدد به سرور هستیم. در صورت عدم اتصال خودکار در زمان مناسب صفحه را مجدد بارگزاری (ریفرش) کنید.
                </div>
            </div>
        }
    </>
}
